import { useEffect, useId, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import {
  setDisplayCurrency,
  setDisplayProfile,
  setPaperSize,
  setTenant,
  setTheme,
  toggleLineageOverlay,
} from "@app/slices/prefsSlice";
import { useLocale } from "@hooks/useLocale";
import { THEMES, TENANTS, TENANT_BRANDS, type Theme } from "@oxbo/tokens";
import { LOCALES, LOCALE_META } from "@i18n/locales";
import { useGetProfilesQuery } from "@units/unitsApi";
import { useCapabilities } from "@components/catalog/useCapabilities";
import { CAPABILITIES } from "@api/capabilities";
import { getProdEmail, isProdAuthEnabled, logoutProd } from "@api/prodAuth";
import { PAPER_SIZES } from "@lib/paperSize";

/** Currencies offered in the display-currency picker (demo data is USD). */
const DISPLAY_CURRENCIES = ["USD", "ARS", "COP", "MXN", "BRL"] as const;

/**
 * Settings cogwheel — anchored at the bottom of the left nav. Opens a popover
 * holding Theme (Dark / Light / Auto), Language (autonyms — English / Español /
 * Português, never flags), the tenant / white-label switch, and the
 * data_admin-only Data-lineage toggle (off by default, still admin-gated).
 * Works whether the nav is expanded or collapsed; collapsed shows only the ⚙.
 */
export function SettingsMenu({ collapsed }: { collapsed: boolean }) {
  const dispatch = useAppDispatch();
  const { locale, t, changeLocale } = useLocale();
  const theme = useAppSelector((s) => s.prefs.theme);
  const tenant = useAppSelector((s) => s.prefs.tenant);
  const { can, capsReady } = useCapabilities();
  // Only show the lineage toggle once the SERVER has confirmed the capability.
  // The role-derived fallback optimistically grants lineage:view to a default
  // data_admin role while the server list loads/errors, which leaked the
  // admin-only toggle to non-admins; gating on capsReady closes that.
  const canLineage = capsReady && can(CAPABILITIES.lineageView);
  const lineageOn = useAppSelector((s) => s.prefs.lineageOverlay);
  const displayProfile = useAppSelector((s) => s.prefs.displayProfile);
  const displayCurrency = useAppSelector((s) => s.prefs.displayCurrency);
  const paperSize = useAppSelector((s) => s.prefs.paperSize);
  // Profiles come from Oxbo-M (api / metric / company profiles). MSW-backed in
  // standalone; empty list simply hides the extra options (Auto still works).
  const { data: profiles = [] } = useGetProfilesQuery();

  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Account block only applies in prod-auth mode (real login/logout). The email
  // is decoded from the current access token for display.
  const prodAuth = isProdAuthEnabled();
  const prodEmail = prodAuth ? getProdEmail() : null;

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    await logoutProd();
    // Reload: initProdAuth sees the manual-logout flag and skips the demo
    // auto-login, so the render-gate lands on the login screen.
    window.location.reload();
  }

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const themeLabelKey = (th: Theme) =>
    th === "dark"
      ? "topbar.theme.dark"
      : th === "light"
        ? "topbar.theme.light"
        : "topbar.theme.auto";

  return (
    <div className="settings-wrap" ref={rootRef}>
      <button
        type="button"
        className={`settings-btn${collapsed ? " collapsed" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={t("settings.open")}
        title={collapsed ? t("settings.title") : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="settings-cog" aria-hidden="true">
          ⚙
        </span>
        {!collapsed && <span className="settings-label">{t("settings.title")}</span>}
      </button>

      {open && (
        <div className="settings-panel" role="dialog" id={panelId} aria-label={t("settings.title")}>
          {/* Theme */}
          <div className="settings-group">
            <div className="settings-heading">{t("topbar.theme")}</div>
            <div className="seg" role="group" aria-label={t("topbar.theme")}>
              {THEMES.map((th) => (
                <button
                  key={th}
                  type="button"
                  aria-pressed={theme === th}
                  onClick={() => dispatch(setTheme(th))}
                >
                  {t(themeLabelKey(th))}
                </button>
              ))}
            </div>
          </div>

          {/* Language — autonyms, no flags */}
          <div className="settings-group">
            <div className="settings-heading">{t("topbar.locale")}</div>
            <div className="locale-names" role="group" aria-label={t("topbar.locale")}>
              {LOCALES.map((lc) => (
                <button
                  key={lc}
                  type="button"
                  aria-pressed={locale === lc}
                  onClick={() => changeLocale(lc)}
                >
                  {LOCALE_META[lc].label}
                </button>
              ))}
            </div>
          </div>

          {/* Unit-system display profile — convert every measurement on the fly */}
          <div className="settings-group">
            <div className="settings-heading">{t("settings.units")}</div>
            <div className="unit-profiles" role="group" aria-label={t("settings.units")}>
              <button
                type="button"
                aria-pressed={displayProfile === null}
                onClick={() => dispatch(setDisplayProfile(null))}
              >
                {t("settings.units.auto")}
              </button>
              {profiles.map((p) => (
                <button
                  key={p.profile_key}
                  type="button"
                  aria-pressed={displayProfile === p.profile_key}
                  onClick={() => dispatch(setDisplayProfile(p.profile_key))}
                >
                  {p.display_name}
                </button>
              ))}
            </div>
            <p className="settings-hint">{t("settings.units.hint")}</p>
          </div>

          {/* Display currency (optional) */}
          <div className="settings-group">
            <div className="settings-heading">{t("settings.currency")}</div>
            <div className="unit-currencies" role="group" aria-label={t("settings.currency")}>
              <button
                type="button"
                aria-pressed={displayCurrency === null}
                onClick={() => dispatch(setDisplayCurrency(null))}
              >
                {t("settings.currency.native")}
              </button>
              {DISPLAY_CURRENCIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={displayCurrency === c}
                  onClick={() => dispatch(setDisplayCurrency(c))}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Report page size (ARCH / AFE export) */}
          <div className="settings-group">
            <div className="settings-heading">{t("settings.paper")}</div>
            <div className="unit-currencies" role="group" aria-label={t("settings.paper")}>
              <button
                type="button"
                aria-pressed={paperSize === null}
                onClick={() => dispatch(setPaperSize(null))}
              >
                {t("settings.paper.auto")}
              </button>
              {PAPER_SIZES.map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={paperSize === p}
                  onClick={() => dispatch(setPaperSize(p))}
                >
                  {p}
                </button>
              ))}
            </div>
            <p className="settings-hint">{t("settings.paper.hint")}</p>
          </div>

          {/* Tenant / white-label */}
          <div className="settings-group">
            <div className="settings-heading">{t("settings.tenant")}</div>
            <div className="tenant-list" role="group" aria-label={t("settings.tenant")}>
              {TENANTS.map((tn) => (
                <button
                  key={tn}
                  type="button"
                  aria-pressed={tenant === tn}
                  onClick={() => dispatch(setTenant(tn))}
                >
                  <span className="tenant-mark" aria-hidden="true">
                    {TENANT_BRANDS[tn].initial}
                  </span>
                  {TENANT_BRANDS[tn].wordmark}
                </button>
              ))}
            </div>
          </div>

          {/* Data-lineage toggle — gated on the lineage:view capability (§ACL) */}
          {canLineage && (
            <div className="settings-group">
              <div className="settings-heading">
                {t("topbar.lineage")}{" "}
                <span className="admin-badge">{t("topbar.lineage.adminOnly")}</span>
              </div>
              <div className="lineage-control">
                <button
                  type="button"
                  className="switch"
                  role="switch"
                  aria-checked={lineageOn}
                  aria-label={t("topbar.lineage")}
                  onClick={() => dispatch(toggleLineageOverlay())}
                />
                <span>{lineageOn ? t("topbar.lineage.on") : t("topbar.lineage.off")}</span>
              </div>
            </div>
          )}

          {/* Account / session — prod-auth only (real login/logout) */}
          {prodAuth && (
            <div className="settings-group">
              <div className="settings-heading">{t("settings.account")}</div>
              {prodEmail && (
                <div className="account-email" title={prodEmail}>
                  {prodEmail}
                </div>
              )}
              <button
                type="button"
                className="logout-btn"
                onClick={() => void handleLogout()}
                disabled={loggingOut}
              >
                {loggingOut ? t("auth.logout.busy") : t("auth.logout")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
